/**
 * Íconos SVG en línea (24x24, trazo `currentColor`, dibujados a 16 px). Sin librería de íconos:
 * cada uno es un mapa nombre → trazos. Decorativos (`aria-hidden`): el texto o `aria-label` va en el botón.
 */
import type { ReactNode } from 'react';

const TRAZOS = {
  bug: (
    <>
      <path d="M8 7a4 4 0 0 1 8 0v1H8z" />
      <rect x="7" y="8" width="10" height="11" rx="5" />
      <path d="M12 12v7M3 12h4M17 12h4M4 6l3 2M20 6l-3 2M4 19l3-2M20 19l-3-2" />
    </>
  ),
  req: <path d="M7 4h10v17l-5-3.5L7 21z" />,
  con: <path d="M9.2 9a3 3 0 1 1 4.4 2.6c-.9.5-1.6 1.1-1.6 2.2M12 17.5v.1" />,
  lapiz: (
    <>
      <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16z" />
      <path d="M13.5 6.5l4 4" />
    </>
  ),
  x: <path d="M6 6l12 12M18 6L6 18" />,
  expandir: <path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" />,
  contraer: <path d="M20 10h-6V4M4 14h6v6M14 10l7-7M10 14l-7 7" />,
  tablero: (
    <>
      <rect x="3" y="4" width="5" height="16" rx="1.5" />
      <rect x="10" y="4" width="5" height="10" rx="1.5" />
      <rect x="17" y="4" width="4" height="13" rx="1.5" />
    </>
  ),
  cuenta: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c1-4 4-6 8-6s7 2 8 6" />
    </>
  ),
  doc: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </>
  ),
  personas: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.8-3.4 3.2-5 6.5-5s5.7 1.6 6.5 5" />
      <path d="M16 4.7a3.5 3.5 0 0 1 0 6.6M18 15.3c1.9.6 3.1 2 3.5 4.7" />
    </>
  ),
  ajustes: (
    <>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="17" r="2" />
    </>
  ),
  mas: <path d="M12 5v14M5 12h14" />,
  lupa: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4-4" />
    </>
  ),
  fuera: <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  /** Círculo tachado: tarea descartada. */
  prohibido: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M6.5 6.5l11 11" />
    </>
  ),
  clip: (
    <path d="M20 11.5l-8 8a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10 17.5a2 2 0 0 1-3-3l7.5-7.5" />
  ),
  bajar: <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />,
  papelera: <path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" />,
  lista: <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />,
  gota: <path d="M12 3.5c3.5 4.2 6 7.2 6 10.2a6 6 0 0 1-12 0c0-3 2.5-6 6-10.2z" />,
  izq: <path d="M15 5l-7 7 7 7" />,
  der: <path d="M9 5l7 7-7 7" />,
  salir: <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M10 8l-4 4 4 4M6 12h10" />,
  /** Estados de carga: error, reintentar y sección vacía. */
  alerta: (
    <>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4M12 17v.1" />
    </>
  ),
  recargar: <path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v5h-5" />,
  /** Subtareas: rama (es parte de una tarea madre) y eslabón (vincular una existente). */
  rama: <path d="M6 4v10a4 4 0 0 0 4 4h8M14 14l4 4-4 4" />,
  enlace: (
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  ),
  /** Bloqueos: tarea bloqueada (marca de la tarjeta, «Agregar bloqueo» y el filtro). */
  candado: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  /** Fecha límite (CDT-76): marca de la tarjeta, la lista y el filtro «Solo atrasadas». */
  calendario: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3v4M15 3v4" />
    </>
  ),
  vacio: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 15h6" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type NombreIcono = keyof typeof TRAZOS;

export function Icono({ nombre, tamano = 16 }: { nombre: NombreIcono; tamano?: number }) {
  return (
    <svg
      className="icono"
      width={tamano}
      height={tamano}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {TRAZOS[nombre]}
    </svg>
  );
}
